import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../hooks/useAuth';
import { useSalesforce } from '../hooks/useSalesforce';
import { useWorkspace } from '../context/WorkspaceContext';
import WorkspaceManagement from '../components/WorkspaceManagement';
import WorkspaceModal from '../components/WorkspaceModal';
import { supabase } from '../services/supabaseClient';

export default function WorkspacesPage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { orgId: urlOrgId } = useParams<{ orgId: string }>();
  const { orgs, isSuperAdmin } = useSalesforce({ skipInitialFetch: false });
  const { setCurrentOrgId } = useWorkspace();
  const [selectedOrg, setSelectedOrg] = useState<any>(null);
  const [accountData, setAccountData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showWorkspaceModal, setShowWorkspaceModal] = useState(false);

  // Set the current org in workspace context
  useEffect(() => {
    if (selectedOrg?.id) {
      setCurrentOrgId(selectedOrg.id);
    }
  }, [selectedOrg, setCurrentOrgId]);

  // Fetch account data for subscription tier
  useEffect(() => {
    if (!user || !selectedOrg) return;

    const fetchAccountData = async () => {
      try {
        const { data, error } = await supabase
          .from('accounts')
          .select('id, subscription_tier')
          .eq('id', selectedOrg.account_id)
          .single();
        
        if (error) throw error;
        setAccountData(data);
      } catch (err) {
        console.error('Error fetching account data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAccountData();
  }, [user, selectedOrg]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Handle org context from URL or auto-select
  useEffect(() => {
    if (urlOrgId && orgs.length > 0) {
      const org = orgs.find(o => o.id === urlOrgId);
      if (org) {
        setSelectedOrg(org);
      }
    } else if (orgs.length === 1 && !selectedOrg) {
      setSelectedOrg(orgs[0]);
    } else if (orgs.length > 0 && !urlOrgId && !selectedOrg) {
      // Redirect to first org if no org specified
      navigate(`/${orgs[0].id}/workspaces`, { replace: true });
    }
  }, [urlOrgId, orgs, selectedOrg, navigate]);

  const handleOrgChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const orgId = e.target.value;
    navigate(`/${orgId}/workspaces`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar onSignOut={handleSignOut} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <a href={urlOrgId ? `/${urlOrgId}/dashboard` : '/dashboard'} className="text-gray-400 hover:text-gray-500">
                  <svg className="flex-shrink-0 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                  </svg>
                  <span className="sr-only">Home</span>
                </a>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="flex-shrink-0 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <a href="/orgs" className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700">Organizations</a>
                </div>
              </li>
              {selectedOrg && (
                <li>
                  <div className="flex items-center">
                    <svg className="flex-shrink-0 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="ml-4 text-sm font-medium text-gray-500">{selectedOrg.name}</span>
                  </div>
                </li>
              )}
              <li>
                <div className="flex items-center">
                  <svg className="flex-shrink-0 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="ml-4 text-sm font-medium text-gray-700" aria-current="page">Workspaces</span>
                </div>
              </li>
            </ol>
          </nav>
        </div>

        <div className="mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Workspaces</h1>
              {selectedOrg && (
                <p className="mt-1 text-sm text-gray-600">
                  Manage AI analysis workspaces for {selectedOrg.name}
                </p>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Add Workspace button - hidden for now */}
              
              {/* Org selector - only show if multiple orgs */}
              {orgs.length > 1 && (
                <div>
                  <label htmlFor="org-select" className="sr-only">
                    Select Organization
                  </label>
                  <select
                    id="org-select"
                    value={selectedOrg?.id || ''}
                    onChange={handleOrgChange}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="">Select an organization</option>
                    {orgs.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

        {!selectedOrg ? (
          <div className="bg-white shadow rounded-lg p-6">
            <p className="text-gray-500 text-center">
              {orgs.length === 0 
                ? "No organizations found. Please connect a Salesforce org first." 
                : "Please select an organization to view its workspaces."}
            </p>
          </div>
        ) : isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <WorkspaceManagement 
            orgId={selectedOrg.id}
            accountId={selectedOrg.account_id}
            subscriptionTier={accountData?.subscription_tier || 'basic'}
          />
        )}
        
        {/* Workspace Modal */}
        {showWorkspaceModal && selectedOrg && (
          <WorkspaceModal
            orgId={selectedOrg.id}
            workspace={null}
            onClose={() => setShowWorkspaceModal(false)}
          />
        )}
      </div>
    </div>
  );
}